/**
 * HrCorrections — R152 · Feb 2026
 *
 * HR-only tool to credit/debit leave balances, correct attendance
 * records and patch WFH decisions for any employee — with a full
 * audit trail. Sub-tab of the HR area.
 *
 * Route: /app/hr/corrections   (hr.manage permission)
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Wallet, CalendarCheck, Home, ShieldAlert, History } from "lucide-react";
import { fmtRelative } from "@/lib/format";

const TABS = [
  { id: "leave", label: "Leave balance", icon: Wallet },
  { id: "attendance", label: "Attendance", icon: CalendarCheck },
  { id: "wfh", label: "WFH", icon: Home },
  { id: "audit", label: "Audit trail", icon: History },
];

export default function HrCorrections() {
  const { activeWorkspaceId } = useAuth();
  const [tab, setTab] = useState("leave");
  const [members, setMembers] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    (async () => {
      try {
        const [m, lt] = await Promise.all([
          api.get(`/workspaces/${activeWorkspaceId}/members`),
          api.get(`/workspaces/${activeWorkspaceId}/leave-types`),
        ]);
        const rows = Array.isArray(m.data) ? m.data : (m.data?.items || []);
        setMembers(rows.map((r) => ({
          id: r.user_id || r.id,
          label: r.user?.name || r.name || r.user?.email || r.email || "Unknown",
        })).filter((r) => r.id && r.label).sort((a, b) => a.label.localeCompare(b.label)));
        setLeaveTypes(Array.isArray(lt.data) ? lt.data : []);
      } catch (e) { toast.error(apiErrorMessage(e, "Failed to load")); }
    })();
  }, [activeWorkspaceId]);

  return (
    <div className="min-h-screen" data-testid="hr-corrections-page">
      <PageHeader
        eyebrow="HR · Admin"
        title="HR Corrections"
        description="Credit / debit leave balances, correct attendance and patch WFH for any employee. Every change is logged."
      />
      <div className="px-4 sm:px-6 md:px-10 py-6 space-y-4">
        <div className="border border-amber-300 bg-amber-50 px-3 py-2 flex items-start gap-2 text-xs" data-testid="hr-corrections-warning">
          <ShieldAlert className="w-4 h-4 mt-0.5 text-amber-700 shrink-0" />
          <span className="text-amber-900">These are manual overrides. Every action lands in the audit trail with your name attached. Use with care.</span>
        </div>
        <div className="flex gap-1 border-b border-border overflow-x-auto" data-testid="hr-corrections-tabs">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={`px-3 py-2 text-xs font-medium flex items-center gap-1.5 border-b-2 ${tab === id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid={`hr-corrections-tab-${id}`}
            >
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
        </div>
        {tab === "leave" && <LeaveBalanceForm members={members} leaveTypes={leaveTypes} wsId={activeWorkspaceId} />}
        {tab === "attendance" && <AttendanceForm members={members} wsId={activeWorkspaceId} />}
        {tab === "wfh" && <WfhForm members={members} wsId={activeWorkspaceId} />}
        {tab === "audit" && <AuditView members={members} wsId={activeWorkspaceId} />}
      </div>
    </div>
  );
}

function MemberSelect({ value, onChange, members, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 text-sm" data-testid={testid}><SelectValue placeholder="Select employee…" /></SelectTrigger>
      <SelectContent>
        {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function LeaveBalanceForm({ members, leaveTypes, wsId }) {
  const [userId, setUserId] = useState("");
  const [ltId, setLtId] = useState("");
  const [delta, setDelta] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!userId || !ltId || !delta) { toast.error("Employee, leave type, and delta are required"); return; }
    setBusy(true);
    try {
      await api.post(`/workspaces/${wsId}/hr-corrections/leave-balance`, {
        user_id: userId, leave_type_id: ltId, delta: Number(delta), note: note || null,
      });
      toast.success(`Adjusted ${Number(delta) > 0 ? "+" : ""}${delta} days`);
      setDelta(""); setNote("");
    } catch (e) { toast.error(apiErrorMessage(e, "Adjust failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-border p-4 max-w-xl space-y-3" data-testid="hr-leave-form">
      <div><Label className="text-xs">Employee</Label><MemberSelect value={userId} onChange={setUserId} members={members} testid="hr-leave-user" /></div>
      <div>
        <Label className="text-xs">Leave type</Label>
        <Select value={ltId} onValueChange={setLtId}>
          <SelectTrigger className="h-9 text-sm" data-testid="hr-leave-type"><SelectValue placeholder="Select leave type…" /></SelectTrigger>
          <SelectContent>
            {leaveTypes.map((lt) => <SelectItem key={lt.id} value={lt.id}>{lt.name} ({lt.code})</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs">Delta (days) — positive credits, negative debits</Label>
        <Input type="number" step="0.5" value={delta} onChange={(e) => setDelta(e.target.value)} placeholder="e.g. 2 or -1.5" className="h-9 text-sm" data-testid="hr-leave-delta" />
      </div>
      <div><Label className="text-xs">Reason / note *</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Compensatory credit for weekend on-call" className="h-9 text-sm" data-testid="hr-leave-note" /></div>
      <Button className="rounded-none" onClick={submit} disabled={busy || !note.trim()} data-testid="hr-leave-submit">{busy ? "Saving…" : "Apply correction"}</Button>
    </div>
  );
}

function AttendanceForm({ members, wsId }) {
  const [form, setForm] = useState({ user_id: "", date: new Date().toISOString().slice(0, 10), status: "", check_in: "", check_out: "", hours_worked: "", note: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.user_id || !form.date) { toast.error("Employee + date required"); return; }
    setBusy(true);
    try {
      const payload = { user_id: form.user_id, date: form.date, note: form.note || null };
      if (form.status) payload.status = form.status;
      if (form.check_in) payload.check_in = new Date(form.check_in).toISOString();
      if (form.check_out) payload.check_out = new Date(form.check_out).toISOString();
      if (form.hours_worked !== "") payload.hours_worked = Number(form.hours_worked);
      await api.post(`/workspaces/${wsId}/hr-corrections/attendance`, payload);
      toast.success("Attendance corrected");
      setForm((f) => ({ ...f, status: "", check_in: "", check_out: "", hours_worked: "", note: "" }));
    } catch (e) { toast.error(apiErrorMessage(e, "Correction failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-border p-4 max-w-xl space-y-3" data-testid="hr-attend-form">
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs">Employee</Label><MemberSelect value={form.user_id} onChange={(v) => set("user_id", v)} members={members} testid="hr-attend-user" /></div>
        <div><Label className="text-xs">Date</Label><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="h-9 text-sm" data-testid="hr-attend-date" /></div>
        <div>
          <Label className="text-xs">Status</Label>
          <Select value={form.status} onValueChange={(v) => set("status", v)}>
            <SelectTrigger className="h-9 text-sm" data-testid="hr-attend-status"><SelectValue placeholder="Keep as-is" /></SelectTrigger>
            <SelectContent>
              {["present", "absent", "half_day", "on_leave", "holiday", "week_off"].map((s) => (
                <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Hours worked</Label><Input type="number" step="0.25" value={form.hours_worked} onChange={(e) => set("hours_worked", e.target.value)} className="h-9 text-sm" data-testid="hr-attend-hours" /></div>
        <div><Label className="text-xs">Check-in</Label><Input type="datetime-local" value={form.check_in} onChange={(e) => set("check_in", e.target.value)} className="h-9 text-sm" data-testid="hr-attend-in" /></div>
        <div><Label className="text-xs">Check-out</Label><Input type="datetime-local" value={form.check_out} onChange={(e) => set("check_out", e.target.value)} className="h-9 text-sm" data-testid="hr-attend-out" /></div>
      </div>
      <div><Label className="text-xs">Reason / note</Label><Input value={form.note} onChange={(e) => set("note", e.target.value)} placeholder="e.g. Biometric failed; corrected from manual sheet" className="h-9 text-sm" data-testid="hr-attend-note" /></div>
      <Button className="rounded-none" onClick={submit} disabled={busy || !form.note.trim()} data-testid="hr-attend-submit">{busy ? "Saving…" : "Apply correction"}</Button>
    </div>
  );
}

function WfhForm({ members, wsId }) {
  const [form, setForm] = useState({ user_id: "", date: new Date().toISOString().slice(0, 10), approved: "", reason: "", note: "", delete: false });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.user_id || !form.date) { toast.error("Employee + date required"); return; }
    setBusy(true);
    try {
      const payload = { user_id: form.user_id, date: form.date, note: form.note || null, delete: !!form.delete };
      if (!form.delete) {
        if (form.approved !== "") payload.approved = form.approved === "true";
        if (form.reason) payload.reason = form.reason;
      }
      await api.post(`/workspaces/${wsId}/hr-corrections/wfh`, payload);
      toast.success(form.delete ? "WFH record deleted" : "WFH record patched");
      setForm((f) => ({ ...f, approved: "", reason: "", note: "", delete: false }));
    } catch (e) { toast.error(apiErrorMessage(e, "Correction failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-border p-4 max-w-xl space-y-3" data-testid="hr-wfh-form">
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs">Employee</Label><MemberSelect value={form.user_id} onChange={(v) => set("user_id", v)} members={members} testid="hr-wfh-user" /></div>
        <div><Label className="text-xs">Date</Label><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="h-9 text-sm" data-testid="hr-wfh-date" /></div>
        <div>
          <Label className="text-xs">Approve / reject</Label>
          <Select value={form.approved} onValueChange={(v) => set("approved", v)} disabled={form.delete}>
            <SelectTrigger className="h-9 text-sm" data-testid="hr-wfh-approve"><SelectValue placeholder="Keep as-is" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="true">Approve</SelectItem>
              <SelectItem value="false">Reject</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Reason</Label><Input value={form.reason} onChange={(e) => set("reason", e.target.value)} disabled={form.delete} className="h-9 text-sm" data-testid="hr-wfh-reason" /></div>
      </div>
      <label className="inline-flex items-center gap-2 text-xs">
        <input type="checkbox" checked={form.delete} onChange={(e) => set("delete", e.target.checked)} data-testid="hr-wfh-delete" />
        Delete this record entirely
      </label>
      <div><Label className="text-xs">Reason / note</Label><Input value={form.note} onChange={(e) => set("note", e.target.value)} placeholder="e.g. Duplicate entry cleared" className="h-9 text-sm" data-testid="hr-wfh-note" /></div>
      <Button className="rounded-none" onClick={submit} disabled={busy || !form.note.trim()} data-testid="hr-wfh-submit">{busy ? "Saving…" : "Apply correction"}</Button>
    </div>
  );
}

function AuditView({ members, wsId }) {
  const [rows, setRows] = useState([]);
  const [filterUser, setFilterUser] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!wsId) return;
    setBusy(true);
    (async () => {
      try {
        const { data } = await api.get(`/workspaces/${wsId}/hr-corrections/audit`, {
          params: filterUser ? { user_id: filterUser, limit: 200 } : { limit: 200 },
        });
        setRows(data?.rows || []);
      } catch (e) { toast.error(apiErrorMessage(e, "Failed to load")); }
      finally { setBusy(false); }
    })();
  }, [wsId, filterUser]);
  const memberById = useMemo(() => Object.fromEntries(members.map((m) => [m.id, m.label])), [members]);
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className="w-64"><MemberSelect value={filterUser} onChange={setFilterUser} members={[{ id: "", label: "All employees" }, ...members]} testid="hr-audit-filter" /></div>
        <span className="text-xs text-muted-foreground">{rows.length} record{rows.length === 1 ? "" : "s"}</span>
      </div>
      <div className="border border-border overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-muted/40 border-b border-border">
            <tr>
              <th className="text-left px-3 py-2 uppercase tracking-widest text-[10px] font-semibold w-40">When</th>
              <th className="text-left px-3 py-2 uppercase tracking-widest text-[10px] font-semibold w-32">Kind</th>
              <th className="text-left px-3 py-2 uppercase tracking-widest text-[10px] font-semibold w-40">Employee</th>
              <th className="text-left px-3 py-2 uppercase tracking-widest text-[10px] font-semibold w-40">By</th>
              <th className="text-left px-3 py-2 uppercase tracking-widest text-[10px] font-semibold">Change</th>
              <th className="text-left px-3 py-2 uppercase tracking-widest text-[10px] font-semibold">Note</th>
            </tr>
          </thead>
          <tbody>
            {busy && rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Loading…</td></tr>}
            {!busy && rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No corrections logged yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-border hover:bg-muted/20" data-testid={`hr-audit-row-${r.id}`}>
                <td className="px-3 py-2" title={r.at}>{fmtRelative(r.at)}</td>
                <td className="px-3 py-2 font-mono text-[10px]">{r.kind}</td>
                <td className="px-3 py-2">{r.target_user_name || memberById[r.target_user_id] || r.target_user_id?.slice(0, 8)}</td>
                <td className="px-3 py-2 text-muted-foreground">{r.actor_name}</td>
                <td className="px-3 py-2 text-[10px] font-mono whitespace-pre-wrap max-w-[240px]">
                  {r.before && <div className="text-rose-700">- {JSON.stringify(r.before).slice(0, 100)}</div>}
                  {r.after && <div className="text-emerald-700">+ {JSON.stringify(r.after).slice(0, 100)}</div>}
                </td>
                <td className="px-3 py-2 text-muted-foreground max-w-[280px]">{r.note || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
